import { normalizeTag } from "./format";

export const MANUAL_PREFIX = "PL1000";
export const AUTO_PLACEHOLDER = "616100022789744";

export type TagInputMode = "manual" | "auto";

const TAG_PREFIXES = ["pl1000", "pl", "616"] as const;

/** Strip country/farm prefixes so last digits match while typing. */
export function stripTagPrefix(query: string): string {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return "";
  }
  for (const prefix of TAG_PREFIXES) {
    if (needle === prefix) {
      return "";
    }
    if (needle.startsWith(prefix)) {
      return needle.slice(prefix.length);
    }
  }
  return needle;
}

/** Fragment actually typed in a tag field, used for live filtering. */
export function typedTagFilterQuery(mode: TagInputMode, input: string): string {
  const trimmed = input.trim();
  if (!trimmed) {
    return "";
  }
  if (mode === "manual") {
    const upper = trimmed.toUpperCase();
    if (upper === MANUAL_PREFIX || upper === "PL") {
      return "";
    }
  }
  return stripTagPrefix(normalizeTag(trimmed).toLowerCase());
}

function compactTag(value: string): string {
  return normalizeTag(value).toLowerCase().replace(/[\s-]/g, "");
}

export function tagMatchesFilter(tag: string, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  const haystack = compactTag(tag);
  const compactNeedle = needle.replace(/[\s-]/g, "");
  if (haystack.includes(compactNeedle)) {
    return true;
  }
  const stripped = stripTagPrefix(compactNeedle);
  return Boolean(stripped) && stripped !== compactNeedle && haystack.includes(stripped);
}

export function filterItemsByTagQuery<T extends { tag: string }>(items: T[], query: string): T[] {
  if (!query) {
    return items;
  }
  const needle = query.trim().toLowerCase();
  const exact: T[] = [];
  const suffix: T[] = [];
  const rest: T[] = [];
  for (const item of items) {
    const haystack = compactTag(item.tag);
    const compactNeedle = needle.replace(/[\s-]/g, "");
    if (
      haystack === compactNeedle ||
      haystack === `pl${compactNeedle}` ||
      haystack === `pl1000${compactNeedle}`
    ) {
      exact.push(item);
      continue;
    }
    if (haystack.endsWith(compactNeedle)) {
      suffix.push(item);
      continue;
    }
    if (tagMatchesFilter(item.tag, needle)) {
      rest.push(item);
    }
  }
  return [...exact, ...suffix, ...rest];
}

export function formatAutoTag(inputValue: string): string {
  const tag = inputValue.trim() || AUTO_PLACEHOLDER;
  if (tag.startsWith("616")) {
    return `PL${tag.slice(3)}`;
  }
  if (!tag.toUpperCase().startsWith("PL")) {
    return `PL${tag}`;
  }
  return tag;
}

export function formatManualTag(inputValue: string): string {
  const tag = inputValue.trim();
  if (!tag) {
    return "";
  }
  if (!tag.toUpperCase().startsWith("PL")) {
    return `${MANUAL_PREFIX}${tag}`;
  }
  return tag;
}

export function formatTagForMode(mode: TagInputMode, inputValue: string): string {
  return mode === "auto" ? formatAutoTag(inputValue) : formatManualTag(inputValue);
}

export function protectManualPrefixValue(value: string): string {
  if (value.toUpperCase().startsWith(MANUAL_PREFIX)) {
    return value;
  }
  const suffix = value.replace(/^PL1000?/i, "").replace(/^PL/i, "");
  return `${MANUAL_PREFIX}${suffix}`;
}

export function matchActiveTag<T extends { tag: string; normalized: string }>(
  tags: T[],
  raw: string,
): T | null {
  const normalized = normalizeTag(raw).replace(/[\s-]/g, "");
  const exact =
    tags.find((item) => {
      const itemTag = normalizeTag(item.normalized || item.tag).replace(/[\s-]/g, "");
      return itemTag === normalized;
    }) ?? null;
  if (exact) {
    return exact;
  }
  const query = stripTagPrefix(normalized.toLowerCase());
  if (!query) {
    return null;
  }
  const matches = tags.filter((item) => tagMatchesFilter(item.tag, query));
  if (matches.length === 1) {
    return matches[0];
  }
  const suffix = matches.filter((item) =>
    normalizeTag(item.tag).toLowerCase().endsWith(query),
  );
  return suffix.length === 1 ? suffix[0] : null;
}
